import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/stores/auth';

interface TurnstileApi {
  render: (element: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let loading: Promise<TurnstileApi> | null = null;

/** Loads Cloudflare's script once, the first time a form needs it. */
function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('Turnstile did not start')));
    script.onerror = () => {
      loading = null; // try again next time
      script.remove();
      reject(new Error('Turnstile did not load'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/**
 * Cloudflare Turnstile's check that someone isn't a bot, for the sign-up, login
 * and password-reset forms. Most people never click anything: it finishes by
 * itself while they type. When the server hasn't switched it on, there's
 * nothing to show and `ready` is always true.
 */
export function useHumanCheck(action: 'login' | 'register' | 'forgot') {
  const siteKey = useAuth((s) => s.config.turnstileSiteKey);
  const [token, setToken] = useState<string | null>(null);
  const [problem, setProblem] = useState(false);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const widget = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey || !container) return;
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled) return;
        widget.current = turnstile.render(container, {
          sitekey: siteKey,
          action,
          theme: 'auto',
          size: 'flexible',
          callback: (answer: string) => {
            setToken(answer);
            setProblem(false);
          },
          'expired-callback': () => setToken(null),
          'error-callback': () => {
            setToken(null);
            setProblem(true);
          },
        });
      })
      .catch(() => !cancelled && setProblem(true));
    return () => {
      cancelled = true;
      if (widget.current) window.turnstile?.remove(widget.current);
      widget.current = null;
      setToken(null);
    };
  }, [siteKey, action, container]);

  /** Each answer works once: call after every try so the next one gets a fresh answer. */
  const reset = useCallback(() => {
    setToken(null);
    if (widget.current) window.turnstile?.reset(widget.current);
  }, []);

  const element = siteKey ? (
    <div className="flex flex-col items-center gap-1">
      <div ref={setContainer} className="min-h-[65px] w-full" />
      {problem && (
        <p role="alert" className="text-center text-xs text-danger">
          The check that you&apos;re not a bot couldn&apos;t finish. Reload the page, or turn off anything that blocks challenges.cloudflare.com.
        </p>
      )}
    </div>
  ) : null;

  return { ready: !siteKey || !!token, token: token ?? undefined, element, reset };
}

export const WAIT_FOR_CHECK = "One moment: the check that you're not a bot is still running.";
